/**
 * Plan fields that kit.js's toPlan doesn't map yet (items 186, 199, 200).
 *
 *   sittings_total, sittings_done ........ numbers or null (not tracked)
 *   instruction_template_ids ............. the day-wise / general templates the plan
 *                                          carries, first one first; older plans only
 *                                          have instruction_template_id
 *   schedules rows with state "waiting_for_status" are status-based instructions
 *   that only get a date once the plan reaches their status.
 *   priority, estimated_cost, actual_cost, internal_notes ... the old plan form's fields
 *   acceptance_status .................... pending | accepted | declined; NULL reads as accepted (migration 204)
 */

/** patient_treatments.priority → label (old TreatmentPlanForm). */
export const PLAN_PRIORITIES = [["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]];
/** patient_treatments.acceptance_status → label. */
export const ACCEPTANCE = [["pending", "Waiting for the patient"], ["accepted", "Accepted"], ["declined", "Declined"]];
export const pairLabel = (pairs, key) => pairs.find(([k]) => k === key)?.[1] || "";
const money = (value) => (value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));
/** ₹12,500 */
export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
/** Log entry types (patient_treatment_logs.log_type, old TreatmentPlanDetails). */
export const LOG_TYPES = [["note", "Note"], ["visit", "Visit"], ["milestone", "Milestone"], ["complication", "Complication"]];

const count = (value) => (value === null || value === undefined || value === "" ? null : Number.isFinite(Number(value)) ? Number(value) : null);

/** The template ids on a plan row; the column may arrive as JSON text or an array. */
export function templateIdsOf(row) {
  let list = row.instruction_template_ids;
  if (typeof list === "string") {
    try { list = JSON.parse(list); } catch { list = null; }
  }
  const ids = Array.isArray(list) ? list.map(Number).filter((n) => Number.isInteger(n) && n > 0) : [];
  if (ids.length) return ids;
  return row.instruction_template_id ? [Number(row.instruction_template_id)] : [];
}

/** Extra fields to spread over toPlan(row). */
export const planExtras = (row) => ({
  sittingsTotal: count(row.sittings_total),
  sittingsDone: count(row.sittings_done),
  templateIds: templateIdsOf(row),
  priority: row.priority || "normal",
  estimatedCost: money(row.estimated_cost),
  actualCost: money(row.actual_cost),
  internalNotes: row.internal_notes || "",
  acceptance: row.acceptance_status || "accepted",
});

/** "2 of 6", "2 done", "6 planned", or null when neither is recorded. */
export function sittingsLabel(plan) {
  const { sittingsTotal: total, sittingsDone: done } = plan;
  if (total === null && done === null) return null;
  if (total === null) return `${done} done`;
  if (done === null) return `${total} planned`;
  return `${done} of ${total}`;
}

/** A template is scheduled only while it is live; rows from before template status existed count as live. */
export const isLiveTemplate = (t) => !t.status || t.status === "live";

const CHANNEL = { voice_call: "Call", both: "WhatsApp and call", whatsapp: "WhatsApp" };

/** A waiting_for_status schedule row → a timeline item (item 200). */
export function toWaitingItem(row, index) {
  const offset = Number(row.trigger_days_offset) || 0;
  const time = String(row.send_time || "09:00:00").slice(0, 5);
  return {
    id: `waiting-${row.template_id}-${row.trigger_status_name}-${index}`,
    templateId: row.template_id || null,
    templateName: row.template_name || "Instruction",
    channel: CHANNEL[row.channel] || "WhatsApp",
    state: "Waiting for status",
    statusName: row.trigger_status_name || "",
    when: null,
    whenLabel: `When the plan reaches ${row.trigger_status_name || "its status"}${offset ? `, ${offset} day${offset === 1 ? "" : "s"} later` : ""} at ${time}`,
    day: null,
  };
}
